import styled from "styled-components";
import { ReactComponent as BackButton } from "assets/icons/icon-back-24.svg";
import { IconWrapper, PreviousIcon } from "common/components/icon";
import { useNavigate } from "react-router";
import {
  TextHeading3,
  TextTitle3,
} from "common/components/Style/new/textStyle";
import { colors } from "common/const/colors";
import { SideBarBackground } from "common/components";
import {
  HEADER_HEIGHT,
  PANEL_WIDTH,
  SIDEBAR_WIDTH_EXPANDED,
} from "common/const/const";
import { usePanel } from "common/utils/panelController";
import { slideIn, slideOut } from "common/components/Style/animations";
import { useEffect, useRef } from "react";

const LayoutWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  width: calc(100% - 3rem);
  gap: 1rem;
  margin: 1.5rem;
  box-sizing: border-box;
`;

export const ContentHeader = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  background-color: var(--White);
  border-radius: var(--border-radius-16);
  padding: ${({ $innerWidth }) =>
    $innerWidth ? `6rem calc(100% - ${$innerWidth})` : "3.5rem 3rem"};
  gap: ${({ gap }) => gap ?? "3rem"};
  box-sizing: border-box;
`;

const RightPanel = styled.aside`
  --anim-width: ${({ $width }) => $width};
  overflow: hidden;
  flex-shrink: 0;
  box-sizing: border-box;

  align-self: flex-start;
  height: calc(100vh - ${HEADER_HEIGHT} - 3rem);
  position: sticky;
  top: calc(${HEADER_HEIGHT} + 1.5rem);

  animation: ${({ $closing }) => ($closing ? slideOut : slideIn)} 0.3s ease-out
    forwards;
`;

const PanelInner = styled.div`
  width: ${({ $width }) => $width};
  height: 100%;
  padding: 2rem;
  box-sizing: border-box;
  overflow-y: auto;
  background-color: var(--White);
  border-radius: var(--border-radius-16);
`;

const DefaultLayout = ({
  title,
  subtitle,
  showBackButton = false,
  showLogo = false,
  innerWidth,
  gap,
  children,
  idName,
  titleRightElement,
}) => {
  const navigate = useNavigate();
  const { panel, removePanel } = usePanel();
  const panelRef = useRef(null);

  useEffect(() => {
    if (panel && !panel.closing && panelRef.current) {
      const el = panelRef.current;
      let rafId;

      const follow = () => {
        window.scrollTo({ left: document.body.scrollWidth });
        rafId = requestAnimationFrame(follow);
      };

      rafId = requestAnimationFrame(follow);

      el.addEventListener("animationend", () => cancelAnimationFrame(rafId), {
        once: true,
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [panel]);

  return (
    <LayoutWrapper id={idName}>
      <SideBarBackground />
      <ContentArea $innerWidth={innerWidth} gap={gap}>
        <ContentHeader>
          {showBackButton && (
            <IconWrapper
              onClick={() => navigate(-1)}
              style={{ marginRight: "1rem" }}>
              <PreviousIcon />
            </IconWrapper>
          )}
          {title && (
            <TextHeading3 style={{ marginRight: "0.5rem" }}>
              {title}
            </TextHeading3>
          )}
          {subtitle && (
            <TextTitle3 color={colors.LightNavy}>{subtitle}</TextTitle3>
          )}
          {titleRightElement && titleRightElement}
        </ContentHeader>
        {children}
      </ContentArea>
      {panel && (
        <RightPanel
          ref={panelRef}
          $width={panel.width || PANEL_WIDTH}
          $closing={panel.closing}
          onAnimationEnd={() => panel.closing && removePanel()}>
          <PanelInner $width={panel.width || PANEL_WIDTH}>
            {panel.content}
          </PanelInner>
        </RightPanel>
      )}
    </LayoutWrapper>
  );
};

const WithOutSideBarLayoutContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  width: 100vw;

  overflow-y: auto;
`;

const WithOutSideBarLayout = ({
  isBackButton = true,
  isScroll = false,
  needBackground = false,
  children,
}) => {
  return (
    <WithOutSideBarLayoutContainer
      style={{
        height: isScroll ? "auto" : "100vh",
        justifyContent: isScroll ? "flex-start" : "center",
        backgroundImage: needBackground
          ? `url(${require("assets/images/worldmap.png")})`
          : "none",
        backgroundColor: colors.LightGray,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}>
      {isBackButton && (
        <BackButton
          style={{
            position: "fixed",
            top: "2rem",
            left: "2rem",
            cursor: "pointer",
            zIndex: 100,
          }}
          onClick={() => {
            if (window.location.pathname === "/") {
              window.location.href = "/";
            } else window.history.back();
          }}
        />
      )}
      {children}
    </WithOutSideBarLayoutContainer>
  );
};

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: ${(props) => props.$align ?? "normal"};
  justify-content: ${(props) => props.$justify ?? "flex-start"};
  gap: ${(props) => props.gap ?? "1rem"};
  box-sizing: border-box;
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${(props) => props.$align ?? "normal"};
  justify-content: ${(props) => props.$justify ?? "flex-start"};
  gap: ${(props) => props.gap ?? "1rem"};
  box-sizing: border-box;
`;

export { DefaultLayout, WithOutSideBarLayout, Row, Column };
